import React, { ChangeEvent, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import axios, { AxiosError } from "axios"
import { ndfDepensesListingRequest, ndfDetailRemove } from "../../../../core/_request"
import { toast } from "react-toastify"
import { API_EXTRACT_HT_TVA_FROM_INVOICE_URL, API_URL_ESPACE_AVENTURIER, API_URL_ESPACE_CONSULTANT } from "../../../../../../../constants"
import { getExtensionAccordingToMimetype } from "../../../../../../../helpers/helper-global"
import swal from "sweetalert2"
import { IConsultantNdfDetailModel } from "@common-models/*"

import InstructionStrip from "./components/InstructionStrip"
import Dropzone from "./components/Dropzone"
import PendingQueue from "./components/PendingQueue"
import HistoryList from "./components/HistoryList"

interface PendingFile {
     file: File
     ht: number | ""
     tva: number | ""
}

interface IInvoiceFileResult {
     items: { amountHt: number; vat: number }[]
     totalAmountHt: number
     totalVat: number
}

interface IExtractionResponse {
     invoices: IInvoiceFileResult[]
     totalAmountHt: number
     totalVat: number
}

interface IPropsModel {
     ndfId: number
     editionMode?: boolean
     onCRUDFacturesFraisCallback?: () => void
}

const NdfDetailsSousTraitant = ({ ndfId, editionMode = true, onCRUDFacturesFraisCallback }: IPropsModel) => {
     const queryClient = useQueryClient()
     const fileInputRef = useRef<HTMLInputElement>(null)

     const getDepensesQuery = useQuery<IConsultantNdfDetailModel[], AxiosError>(["ndfDetails", ndfId], () => ndfDepensesListingRequest(ndfId).then(r => r.data))

     const [showInfo, setShowInfo] = useState(false)
     const [isDragging, setIsDragging] = useState(false)
     const [isSending, setIsSending] = useState(false)
     const [isExtracting, setIsExtracting] = useState(false)
     const [processingId, setProcessingId] = useState<number | string | null>(null)
     const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

     const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length > 0) {
               addFiles(Array.from(e.target.files))
          }
          if (fileInputRef.current) {
               fileInputRef.current.value = ""
          }
     }

     const addFiles = async (files: File[]) => {
          if (!files || files.length === 0) return

          const formData = new FormData()
          files.forEach((file, index) => formData.append(`files[${index}]`, file))

          try {
               setIsExtracting(true)

               const response = await axios.post<IExtractionResponse>(API_EXTRACT_HT_TVA_FROM_INVOICE_URL, formData)

               if (response.data && response.data.invoices) {
                    const { invoices } = response.data

                    const extractedEntries = files.map((file, index) => {
                         const invoiceData = invoices[index]
                         const htValue = parseFloat(invoiceData?.totalAmountHt?.toString() || "0")
                         const vatValue = parseFloat(invoiceData?.totalVat?.toString() || "0")

                         return {
                              file,
                              ht: isNaN(htValue) ? 0 : htValue,
                              tva: isNaN(vatValue) ? 0 : Number(vatValue.toFixed(2)),
                         }
                    })

                    // Update state only after extraction is finished to avoid empty rows in PendingQueue
                    setPendingFiles(prev => [...prev, ...extractedEntries])
                    toast.success("Extraction terminée. Veuillez vérifier les montants HT et TVA.")
               }
          } catch (err) {
               toast.error("Erreur lors de l'extraction. Veuillez saisir les montants manuellement.")
               const manualEntries = files.map(f => ({ file: f, ht: "" as const, tva: "" as const }))
               setPendingFiles(prev => [...prev, ...manualEntries])
          } finally {
               setIsExtracting(false)
          }
     }

     const updatePendingData = (index: number, field: "ht" | "tva", value: string) => {
          const updated = [...pendingFiles]
          updated[index][field] = value === "" ? "" : parseFloat(value)
          setPendingFiles(updated)
     }

     const handleAddDepenses = () => {
          if (pendingFiles.length === 0) return

          const hasInvalidData = pendingFiles.some(item => {
               const ht = Number(item.ht)
               return isNaN(ht) || ht <= 0 || item.ht === "" || item.tva === ""
          })

          if (hasInvalidData) {
               toast.error("Veuillez saisir un montant HT et une TVA valides pour tous les documents.")
               return
          }

          setIsSending(true)
          const formData = new FormData()
          pendingFiles.forEach((item, index) => {
               formData.append(`files[${index}]`, item.file)
               formData.append(`amountsHt[${index}]`, item.ht.toString())
               formData.append(`vats[${index}]`, item.tva.toString())
          })

          axios.post(`${API_URL_ESPACE_CONSULTANT}/ndf/${ndfId}/add-depense-simple-for-st`, formData)
               .then(() => {
                    toast.success("Documents enregistrés avec succès")
                    setPendingFiles([])
                    getDepensesQuery.refetch()
                    if (onCRUDFacturesFraisCallback) onCRUDFacturesFraisCallback()
               })
               .catch(() => toast.error("Erreur lors de l'envoi"))
               .finally(() => setIsSending(false))
     }

     const handleDeleteDepense = (depenseId: number) => {
          swal.fire({
               icon: "warning",
               title: "Supprimer ce justificatif ?",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               confirmButtonColor: "#f1416c",
               cancelButtonText: "Annuler",
          }).then(result => {
               if (result.isConfirmed) {
                    setProcessingId(depenseId)
                    ndfDetailRemove(depenseId)
                         .then(() => {
                              queryClient.setQueryData<IConsultantNdfDetailModel[]>(["ndfDetails", ndfId], oldData => (oldData ? oldData.filter(d => d.id !== depenseId) : []))
                              toast.success("Supprimé.")
                              getDepensesQuery.refetch().then(() => onCRUDFacturesFraisCallback?.())
                         })
                         .finally(() => setProcessingId(null))
               }
          })
     }

     const handleDownloadDocDepense = (googleDriveFileId: string, mime: string, docId: number) => {
          setProcessingId(`dl-${docId}`)
          axios.post(`${API_URL_ESPACE_AVENTURIER}/download-document`, { reference: googleDriveFileId }, { responseType: "blob" })
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data]))
                    const link = document.createElement("a")
                    link.href = url
                    link.setAttribute("download", `justificatif.${getExtensionAccordingToMimetype(mime)}`)
                    document.body.appendChild(link)
                    link.click()
               })
               .finally(() => setProcessingId(null))
     }

     const handleViewJustificatif = (reference: string, mimeType: string, docId: number) => {
          setProcessingId(`view-${docId}`)
          axios.post(`${API_URL_ESPACE_AVENTURIER}/download-document`, { reference }, { responseType: "arraybuffer" })
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }))
                    window.open(url)
               })
               .catch((e: AxiosError) => toast.error(e.response?.data?.detail))
               .finally(() => setProcessingId(null))
     }

     return (
          <div className="card shadow-none  bg-transparent">
               <div className="card-body p-0">
                    {editionMode && (
                         <>
                              <InstructionStrip showInfo={showInfo} setShowInfo={setShowInfo} />

                              {isExtracting ? (
                                   <div
                                        className="rounded border border-primary border-dashed bg-light-primary p-12 mb-10 d-flex flex-column align-items-center justify-content-center shadow-sm"
                                        style={{ borderStyle: "dashed" }}
                                   >
                                        <div className="mb-5 position-relative">
                                             <div className="spinner-border text-primary" style={{ width: "3.5rem", height: "3.5rem", borderWidth: "3px" }} role="status"></div>
                                             <i className="fas fa-magic fs-4 text-primary position-absolute top-50 start-50 translate-middle"></i>
                                        </div>
                                        <h3 className="text-primary fw-bolder fs-3 mb-2">Analyse en cours ...</h3>
                                        <p className="text-gray-600 fw-semibold fs-6 text-center max-w-400px mb-0">Identification automatique des montants HT et TVA.</p>
                                   </div>
                              ) : (
                                   <Dropzone
                                        isDragging={isDragging}
                                        fileInputRef={fileInputRef}
                                        onDragOver={e => {
                                             e.preventDefault()
                                             setIsDragging(true)
                                        }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onDrop={e => {
                                             e.preventDefault()
                                             setIsDragging(false)
                                             if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                                  addFiles(Array.from(e.dataTransfer.files))
                                             }
                                        }}
                                        onFileSelect={handleFileSelect}
                                   />
                              )}
                         </>
                    )}

                    {pendingFiles.length > 0 && !isExtracting && (
                         <PendingQueue
                              files={pendingFiles}
                              isSending={isSending}
                              isExtracting={isExtracting}
                              onUpdateData={updatePendingData}
                              onRemove={idx => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                              onSubmit={handleAddDepenses}
                         />
                    )}

                    {getDepensesQuery.data && getDepensesQuery.data.length > 0 && (
                         <HistoryList
                              data={getDepensesQuery.data}
                              processingId={processingId}
                              editionMode={editionMode}
                              onView={handleViewJustificatif}
                              onDownload={handleDownloadDocDepense}
                              onDelete={handleDeleteDepense}
                         />
                    )}
               </div>
          </div>
     )
}

export default NdfDetailsSousTraitant
